/**
 * Map API / system error keys to counsellor-friendly copy.
 * Keep technical codes out of toasts and alerts across the platform.
 */

const FRIENDLY_BY_KEY = {
	notAuthorised:
		'You do not have permission for this action. Ask a Super Admin to update access under Settings → Roles Permissions, then reload the page.',
	notAuthorized:
		'You do not have permission for this action. Ask a Super Admin to update access under Settings → Roles Permissions, then reload the page.',
	notFound: 'We could not find that item. It may have been removed or you may not have access.',
	alreadyExists: 'This already exists. Please check and try again.',
	missingParameters: 'Some required information is missing. Please review the form and try again.',
	notEditable:
		'This role is locked and cannot be changed. Super Admin access stays full so the platform cannot be locked out.',
	noToken: 'Your session has expired. Please sign in again.',
	invalidToken: 'Your session is no longer valid. Please sign in again.',
	Failed: 'Something went wrong. Please try again.',
	'Role not found': 'That role could not be found. Refresh the page and try again.',
}

const KEY_PATTERN =
	/^(notAuthorised|notAuthorized|notFound|alreadyExists|missingParameters|notEditable|noToken|invalidToken)$/i

/**
 * @param {unknown} raw
 * @param {string} [fallback]
 * @returns {string}
 */
export function toUserFriendlyError(raw, fallback = 'Something went wrong. Please try again.') {
	if (raw == null || raw === '') return fallback

	if (Array.isArray(raw)) {
		const first = raw[0]
		if (first?.errors && typeof first.errors === 'object') {
			const key = Object.keys(first.errors)[0]
			const nested = first.errors[key]
			return toUserFriendlyError(nested, fallback)
		}
		return toUserFriendlyError(first, fallback)
	}

	if (typeof raw === 'object') {
		if (typeof raw.message === 'string') return toUserFriendlyError(raw.message, fallback)
		if (typeof raw.error === 'string') return toUserFriendlyError(raw.error, fallback)
		return fallback
	}

	const text = String(raw).trim()
	if (!text) return fallback

	const mapped = FRIENDLY_BY_KEY[text] || FRIENDLY_BY_KEY[text.toLowerCase?.()]
	if (mapped) return mapped

	if (KEY_PATTERN.test(text)) {
		return FRIENDLY_BY_KEY.notAuthorised
	}

	// CamelCase API keys with no spaces → treat as technical
	if (/^[a-z]+([A-Z][a-z0-9]*)+$/.test(text) && text.length < 48) {
		return fallback
	}

	return text
}

export function isPermissionDeniedError(raw) {
	const text = String(raw || '').trim().toLowerCase()
	return (
		text === 'notauthorised' ||
		text === 'notauthorized' ||
		text.includes('do not have permission') ||
		text.includes('roles permissions')
	)
}

/** Pull the most useful error field from an Axios / RTK / plain error. */
export function extractApiErrorRaw(error) {
	if (error == null) return null
	return (
		error?.response?.data?.error ||
		error?.response?.data?.error_message ||
		error?.response?.data?.message ||
		error?.payload ||
		error?.errorMessage ||
		error?.message ||
		null
	)
}

/** Friendly string for toasts from any API/RTK failure shape. */
export function getFriendlyApiError(
	error,
	fallback = 'Something went wrong. Please try again.',
) {
	return toUserFriendlyError(extractApiErrorRaw(error), fallback)
}
